import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import { Button, Typography, Menu, ListItemButton, Divider } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { OZCustomToggleButton } from "../../../components/admin/common/OZCustomToggleButton";
import { useEffect, useMemo, useRef, useState } from "react";
import OZDataGrid from "../../../components/admin/common/OZDataGrid/OZDataGrid";
import OZGridToolbar from "../../../components/admin/common/OZDataGrid/OZGridToolbar";
import "./manageWidgets.css";
import ManageWidgetsForm from "./ManageWidgetsForm";
import AssignWidgets from "./AssignWidgets";
import DeleteDialog from "../../../components/admin/common/CustomDeleteDialog";
import { showErrorNotification, showSuccessNotification } from "../../../components/admin/common/NotiService";
import { writeAccess } from "../../../services/page-service";
import LoadingScreen from "../../../components/admin/common/OZLoadingScreen/OZLoadingScreen";
import { getWidgets, deleteManageWidgetById, updateWidgetById, createWidget } from "../../../services/api-service";
import { getUserId, getUserList } from "../../../services/auth-service";
import { v4 as uuidv4 } from 'uuid';

const toggleStyle = {
    color: "#212121",
    fontFamily: "Roboto",
    fontSize: "0.75rem",
    fontWeight: 400,
    letterSpacing: "0em",
    backgroundColor: "#F5F8FA",
    minWidth: "5.8125rem",
    height: "1.875rem",
    borderRadius: 0,
    '&.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: '#FFFF',
    }
};

function ManageWidgets() {
    const [tabType, setTabType] = useState('widgets');
    const [openWidgetForm, setOpenWidgetForm] = useState({ isNew: false, show: false });
    const assignWidgetsRef = useRef();
    const [widgetTableData, setWidgetTableData] = useState([]);
    const [widgetGridData, setWidgetGridData] = useState(null);
    const [widgetOnclickRowData, setWidgetOnclickRowData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [widgetList, setWidgetList] = useState([]);

    const tabTypes = useMemo(() => [
        { label: "Widgets", value: "widgets" },
        { label: "Assign", value: "assign" }
    ], []);

    function EditWidgetRowOnMouseEnter(props) {
        const [widgetAnchorE1, setWidgetAnchorE1] = useState(false);
        const anchorRef = useRef();
        const [openWidgetDeleteDialog, setOpenWidgetDeleteDialog] = useState(false);

        const onClickMoreVertIcon = () => {
            setTimeout(() => setWidgetAnchorE1(anchorRef?.current), 1);
        };

        const closeWidgetMorevertMenu = () => {
            setWidgetAnchorE1(false);
        };

        function onClickDelete() {
            setWidgetAnchorE1(false);
            setOpenWidgetDeleteDialog(true);
        }

        const styleListItemButton = useMemo(
            () => ({
                "&.MuiButtonBase-root": {
                    color: '#212121',
                    fontFamily: "Roboto",
                    fontSize: "0.75rem",
                    fontWeight: 400,
                    height: '18px',
                    lineHeight: "14px",
                },
                '&:hover': {
                    borderRadius: "4px",
                },
            }),
            []
        );

        async function deleteWidget() {
            if (!props.data.id) {
                showErrorNotification("Invalid widget ID");
                return;
            }
            setLoading(true);
            try {
                const resp = await deleteManageWidgetById(props.data.id);
                if (resp.Status === "Success") {
                    getWidgetsList();
                    showSuccessNotification(resp.Message);
                } else {
                    throw new Error(resp.Message);
                }
            } catch (e) {
                showErrorNotification(e?.message || "Error in Deleting Widget");
            } finally {
                setLoading(false);
            }
            setOpenWidgetDeleteDialog(false);
        }

        async function onClickEdit() {
            onCLickRow(props);
            setWidgetAnchorE1(false);
        }

        return (
            <div className="oz-manage-widget-morevert-column-cell">
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 400, color: "#000000" }}>
                    {props.value ? "Active" : "Inactive"}
                </Typography>
                {writeAccess() &&
                    <img className="delMoreIcon"
                        onClick={onClickMoreVertIcon}
                        src={`${process.env.PUBLIC_URL}/icons/more_vert.svg`} alt="edit"
                    >
                    </img>}
                <div ref={anchorRef}>
                    <Menu anchorEl={widgetAnchorE1} open={Boolean(widgetAnchorE1)} onClose={closeWidgetMorevertMenu} sx={{
                        "& .MuiPaper-root": {
                            backgroundColor: "#FFFFFF",
                            width: "133px",
                            height: "62px",
                        },
                        "& .MuiMenu-paper": {
                            backgroundColor: "#FFFFFF",
                            boxShadow: "0rem 0.25rem 0.75rem rgba(0, 0, 0, 0.04)",
                            border: "0.0625rem solid #E6E5E6",
                            borderRadius: "12px",
                            marginLeft: "-1.0625rem",
                            marginTop: "1.2rem",
                        },
                    }}>
                        <ListItemButton
                            sx={styleListItemButton}
                            onClick={() => { onClickEdit() }}>
                            Edit
                        </ListItemButton>
                        <ListItemButton
                            onClick={onClickDelete}
                            sx={{ ...styleListItemButton, marginTop: '7px' }}>
                            Delete
                        </ListItemButton>
                    </Menu>
                    {openWidgetDeleteDialog && <DeleteDialog
                        open={openWidgetDeleteDialog}
                        onClose={() => { setOpenWidgetDeleteDialog(false) }}
                        title="Delete Widget"
                        content={`This will delete Widget: ${props.data.name} permanently. You cannot undo this action.`}
                        deleteFunction={deleteWidget} />}
                </div>
            </div>
        );
    };

    const convertJsonToArray = (json) => {
        try {
            let parsedParams = json;
            if (typeof json === 'string') {
                try {
                    parsedParams = JSON.parse(json);
                } catch (e) {
                    console.log("Error in parsing json", e);
                }
            }
            if (parsedParams && typeof parsedParams === 'object' && Object.keys(parsedParams).length > 0) {
                return Object.keys(parsedParams).map(key => ({
                    key: key,
                    value: parsedParams[key],
                    id: uuidv4()
                }));
            }
            return [];
        } catch (e) {
            console.log("Error in converting json to array", e);
            return [];
        }
    }

    async function getWidgetsList() {
        setLoading(true);
        try {
            const resp = await getWidgets();
            if (resp.Status === "Success" && resp.Data && Array.isArray(resp.Data)) {
                setWidgetTableData(resp.Data)
                const reducedData = resp.Data.map(item => ({
                    id: item.id,
                    name: item.name,
                    code: item.code,
                    userRefreshInterval: item.refreshInterval,
                    userWidgetParams: convertJsonToArray(item.widgetParams),
                }))
                setWidgetList(reducedData);
            } else {
                throw new Error(resp.Message);
            }
        } catch (e) {
            console.log("Error in Widgets list", e);
            showErrorNotification(e?.message || "Error in fetching Widgets list");
            setWidgetTableData([]);
        } finally {
            setLoading(false);
        }
    }

    async function fetchUsersData() {
        try {
            let usersResp = JSON.parse(getUserList());
            if (usersResp) {
                setUsers(usersResp.map(user => ({
                    name: user.label,
                    id: user.value
                })));
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setUsers([]);
        }
    };

    async function updateWidget(formData, closeWidgetForm) {
        setLoading(true);
        try {
            const resp = await updateWidgetById(formData.id, { ...formData, userId: getUserId() });
            if (resp.Status === "Success") {
                getWidgetsList();
                showSuccessNotification(`Widget: ${formData.name} Updated Successfully`);
                closeWidgetForm();
            } else {
                throw new Error(resp.Message);
            }
        } catch (e) {
            showErrorNotification(e?.message || "Error in Updating Widget");
        } finally {
            setLoading(false);
        }
    }

    async function addWidget(formData, closeWidgetForm) {
        setLoading(true)
        try {
            const resp = await createWidget({ ...formData, userId: getUserId() });
            if (resp.Status === "Success") {
                getWidgetsList();
                showSuccessNotification(`Widget: ${formData.name} Created Successfully`);
                closeWidgetForm();
            } else {
                throw new Error(resp.Message);
            }
        } catch (e) {
            showErrorNotification(e?.message || "Error in Creating Widget");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        getWidgetsList();
        fetchUsersData();
    }, []);

    const columnDefs = [
        {
            field: "name",
            headerName: "Widget Name",
        },
        {
            field: "code",
            headerName: "Widget Code",
        },
        {
            field: "description",
            headerName: "Widget Description",
        },
        {
            field: "isActive",
            headerName: "Widget Status",
            cellRenderer: EditWidgetRowOnMouseEnter,
        },
    ];

    let buttonStyle = useMemo(
        () => ({
            height: "1.875rem",
            backgroundColor: "#3D8BF8",
            borderRadius: "0.5rem",
            fontSize: "0.875rem",
            fontWeight: 400,
        }),
        []
    );

    const showWidgetForm = () => {
        setOpenWidgetForm({ isNew: true, show: true });
    };

    const closeWidgetForm = () => {
        setOpenWidgetForm(false);
        setOpenWidgetForm({ isNew: false, show: false });
    };

    async function onCLickRow(rowItem) {
        setOpenWidgetForm({ isNew: false, show: true });
        setWidgetOnclickRowData({});
        try {
            if (rowItem?.data) {
                const widgetData = rowItem?.data;
                setWidgetOnclickRowData({
                    code: widgetData?.code || "",
                    name: widgetData?.name || "",
                    description: widgetData?.description || "",
                    refreshInterval: widgetData?.refreshInterval || 0,
                    apiId: widgetData?.apiId || null,
                    isActive: widgetData?.isActive !== undefined ? widgetData.isActive : true,
                    widgetParams: convertJsonToArray(widgetData.widgetParams),
                    id: widgetData?.id || null
                });
            }
        } catch (e) {
            console.log("Error in widget details", e);
            showErrorNotification(e?.message || "Error in widget details");
        }
    }

    return (
        <div className="ozAdminManageWidgetContainer oz-manage-widget-main-container" >
            <Typography
                sx={{ fontSize: "0.75rem", lineHeight: "0.875rem", marginBottom: "0.625rem", color: "#212121", }} alignSelf="center">
                Administration
            </Typography>

            <div className="oz-manage-widget-inner-container">
                <Typography
                    sx={{ fontStyle: "normal", fontWeight: 600, fontSize: "1.5rem", lineHeight: "1.75rem" }} flexGrow={1}>
                    Manage Widgets
                </Typography>
                {writeAccess() && <Button
                    sx={{ ...buttonStyle, width: tabType === 'widgets' ? "9.3125rem" : "11.25rem" }}
                    variant="contained"
                    startIcon={<AddBoxOutlinedIcon />}
                    onClick={tabType === 'widgets' ? showWidgetForm : () => assignWidgetsRef.current?.showAssignForm()}>
                    {tabType === 'widgets' ? "New Widget" : "Assign Widgets"}
                </Button>}
            </div>

            <div style={{ display: "flex", height: "1.875rem", marginTop: '8px' }}>
                <TabContext value={tabType}>
                    <TabList TabIndicatorProps={{ style: { backgroundColor: "#008EFF", marginBottom: "1.125rem" } }}>
                        {tabTypes.map((type, index) => (
                            <OZCustomToggleButton
                                key={type.value}
                                sx={{ ...toggleStyle, borderLeftWidth: (index !== 0 ? 0 : 1) }}
                                onClick={() => setTabType(type.value)}
                                value={type.value}
                            >
                                {type.label}
                            </OZCustomToggleButton>
                        ))}
                    </TabList>
                </TabContext>
                <span style={{ flexGrow: 1, alignSelf: "flex-end" }}>
                    <Divider variant="fullWidth" sx={{ borderColor: "#E6E5E6" }} />
                </span>
            </div>

            {/* Widgets Tab Content */}
            {tabType === 'widgets' && (
                <div>
                    {openWidgetForm.show && openWidgetForm.isNew && (
                        <ManageWidgetsForm
                            updateWidgets={getWidgetsList}
                            onClose={closeWidgetForm}
                            updateWidget={updateWidget}
                            addWidget={addWidget}
                        />
                    )}
                    {openWidgetForm.show && !openWidgetForm.isNew && (
                        <ManageWidgetsForm
                            updateWidgets={getWidgetsList}
                            onClose={closeWidgetForm}
                            data={widgetOnclickRowData}
                            openDialog={true}
                            editAccess={writeAccess()}
                            updateWidget={updateWidget}
                            addWidget={addWidget}
                        />
                    )}
                    <OZGridToolbar
                        showImport={false}
                        refreshGrid={getWidgetsList}
                        title='List of Widgets'
                        searchPlaceholder='Widget Name'
                        rowCount={widgetTableData.length}
                        dataGridRef={widgetGridData}
                        fileName='widgets'
                    />
                    {loading ?
                        (
                            <div style={{ position: 'fixed', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: '0,0,0,0.5', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} >
                                <LoadingScreen />
                            </div>
                        ) :
                        (<>
                            <OZDataGrid
                                columns={columnDefs}
                                data={widgetTableData}
                                setGridRef={setWidgetGridData}
                                rowClickCallback={onCLickRow}
                            />
                        </>
                        )
                    }
                </div>
            )}

            {/* Assign Tab Content */}
            {tabType === 'assign' && (
                <div>
                    <AssignWidgets ref={assignWidgetsRef} users={users} widgets={widgetList} />
                </div>
            )}
        </div>
    );
}

export default ManageWidgets;